import { api } from './api'

/**
 * §12's Open Question 1 (docs/RISK_REGISTER.md) is resolved: `GET /api/me`
 * (server/routes/me.js, `requireAuth`) IS the combined session+profile
 * endpoint — found 2026-09-14 while fixing a real bug (MEET's "전체 프로필
 * 보기" link pointed at the wrong URL because we only had `/auth/me`'s bare
 * identity and no real username to build a profile link from). This
 * supersedes `/auth/me` for MEET's purposes; only the fields MEET actually
 * uses are typed here — `profile.js`/`me.js` return much more (asset
 * library, work history, wallet balances, etc.) that MEET has no use for.
 */
export interface ApiMeResponse {
  ok: boolean
  user: {
    id: string
    email: string | null
  }
  profile: {
    username: string | null
    usernameDisplay: string | null
    displayName: string
    avatarUrl: string
  } | null
}

export interface KeiSession {
  userId: string
  email: string | null
  displayName: string | null
  avatarUrl: string | null
  profileHandle: string | null
  languages: string[]
  organization: Record<string, unknown> | null
}

export async function fetchMe(): Promise<KeiSession | null> {
  try {
    const res = await api.get<ApiMeResponse>('/api/me')
    if (!res?.ok) return null
    const profile = res.profile
    return {
      userId: res.user.id,
      email: res.user.email,
      displayName: profile?.displayName || profile?.usernameDisplay || null,
      avatarUrl: profile?.avatarUrl || null,
      profileHandle: profile?.username || null,
      languages: [],
      organization: null
    }
  } catch {
    return null
  }
}

/**
 * 2026-09-14 — login/signup handled directly inside meet.kei.bio instead of
 * sending the user off to kei.bio and hoping they find their way back (they
 * didn't — real feedback: "홈메인 화면으로 뚝 떨어트림, 어디서 로그인하고
 * 회원가입해야하는지도 몰라요"). Calls the exact same `/auth/*` endpoints
 * kei.bio's own login modal uses (confirmed by reading server/routes/auth.js
 * and App.vue directly) — `verify-otp`'s Set-Cookie is scoped to domain
 * `.kei.bio`, which is exactly why this works from meet.kei.bio's origin too,
 * no separate MEET-side auth needed.
 */
export interface RequestOtpResponse {
  ok: boolean
  sessionToken: string
}
export interface VerifyOtpResponse {
  ok: boolean
  isNewUser: boolean
}
export interface CheckHandleResponse {
  available: boolean
  reason?: 'LENGTH' | 'FORMAT' | 'RESERVED'
}

export function requestOtp(email: string, lang: string = 'ko') {
  return api.post<RequestOtpResponse>('/auth/request-otp', { email, lang })
}

export function verifyOtp(sessionToken: string, otpCode: string) {
  return api.post<VerifyOtpResponse>('/auth/verify-otp', { sessionToken, otpCode })
}

export function checkHandle(handle: string) {
  return api.get<CheckHandleResponse>(`/auth/check-handle?h=${encodeURIComponent(handle)}`)
}

export function setHandle(handle: string) {
  return api.post<{ ok: boolean; handle: string }>('/auth/set-handle', { handle })
}
